var RequestHandler = require('./RequestHandler.js');

var MAX_ROWS = 1000000; // 1,000,000

/**
 * OData interface for requesting chunked data.
 */

/**
 * Gets converted property value from raw data.
 * Navigation properties are parsed.
 *
 * @param {Object} oRow - Raw data row
 * @param {Object} oCol - Column information
 * @param {Array} oCol.keys - Property name or key path for navigation properties
 * @returns {number|string|boolean} - The converted property value
 * @private
 */
function getValue(oRow, oCol) {

	// Get property value
	var value = oCol.keys.reduce(function(obj, key) {
		return obj && obj[key];
	}, oRow);

	return value;
}

/**
 * Function to process the JSON result array from a ODataService.
 *
 * @param {Array} aRows - Data array that contains the received data
 * @param {Array} aCols - Columns that need to be converted
 * @returns {Array} - An array of rows
 *
 * @private
 */
function convertData(aRows, aCols) {
	aCols.forEach(function(col) {
		aRows.forEach(function(row) {
			row[col.property] = getValue(row, col);
		});
	});

	return aRows;
}

/**
 * The function returns array of columns that need special conversion for values.
 * E.g. handling data from association/navigationProperty
 *
 * @param {Object} mSettings - Configuration object
 * @returns {Array} - Collection of columns that need special conversion for their values
 * @private
 */
function getColsToConvert(mSettings) {
	return mSettings.workbook.columns.reduce(function(result, col) {
		var properties;

		// Handle aggregated properties and single properties
		properties = col.property instanceof Array ? col.property : [col.property];
		// Handle unitPoperty which too could be from an association
		if (col.unitProperty) {
			properties.push(col.unitProperty);
		}

		properties.forEach(function(property) {

			// Convert navigation property and date fields
			var aKeys = property.split('/');

			if (aKeys.length > 1) {
				result.push({
					property: property,
					keys: aKeys,
					type: col.type
				});
			}
		});

		return result;
	}, []);
}

/**
 * The function returns a conversion function for raw data.
 *
 * @param {Object} mSettings Configuration object
 * @returns {function} Conversion function
 */
function getDataConverter(mSettings) {
	var aColumns = getColsToConvert(mSettings);

	return function(aRows) {
		return convertData(aRows, aColumns);
	};
}

/**
 * The function processes the dataURL and adds any missing $skip or $top before initial use.
 *
 * @param {string} sDataUrl
 * @param {number} iSkip - The amount of items that are already present and will be skipped
 * @param {number} iTop - The amount of items that should be requested with this query
 * @returns {string} processed data URL
 */
function processDataUrl(sDataUrl, iSkip, iTop) {
	var mDataUrl, reSkip = /\$skip\=[0-9]+/, reTop = /\$top\=[0-9]+/;

	if (!sDataUrl) {
		return '';
	}

	mDataUrl = URI.parse(sDataUrl);

	mDataUrl.query = mDataUrl.query || '';
	// Add missing $skip if needed
	if (!reSkip.test(mDataUrl.query)) {
		mDataUrl.query += (mDataUrl.query.length ? '&' : '') + '$skip=' + iSkip;
	}
	// Add missing $top if needed
	if (!reTop.test(mDataUrl.query)) {
		mDataUrl.query += '&$top=' + iTop;
	}

	return (URI.serialize || URI.build)(mDataUrl);
}

/**
 * The function requests several chunks of data until the maximum
 * amount of data is fetched.
 *
 * @param {Object} mSettings - Configuration object
 * @param {function} fnProcessCallback - Callback function that is triggered when data is received
 * @returns {Object} - Object reference that allows to cancel the current processing
 */
function requestData(mSettings, fnProcessCallback) {
	var iAvailableRows = 0;
	var mDataSource = mSettings.dataSource;
	var iTotalRows = Math.min(mDataSource.count || MAX_ROWS, MAX_ROWS);
	var iBatchSize = Math.min(mDataSource.sizeLimit || MAX_ROWS, mSettings.batchSize || MAX_ROWS, iTotalRows);
	var bCancelled = false;
	var dataUrl = processDataUrl(mDataSource.dataUrl, 0, iBatchSize);
	var fnConvertData = getDataConverter(mSettings);
	var mRequest;

	/**
	 * Nested function to remove not used information from the URL
	 *
	 * @param {string} url - A URL that may contain a path, hash and request parameters
	 * @returns {string} - A clean URL
	 */
	function cleanUrl(url) {
		var mUri;

		if (!url) {
			return '';
		}

		mUri = URI.parse(url);

		mUri.path = mUri.path || '';
		if (mUri.path.slice(-1) !== '/') {
			mUri.path = mUri.path + '/';
		}
		delete mUri.query;
		delete mUri.hash;
		delete mUri.fragment;

		return (URI.serialize || URI.build)(mUri);
	}


	/**
	 * Creates the download URL for the next query.
	 *
	 * @param {number} iSkip - The amount of items that are already present and will be skipped
	 * @param {number} iTop - The amount of items that should be requested with this query
	 * @param {string} sNextUrl - A reference to the next bulk of data that was returned by the previous request
	 * @returns {string} - The URL for the next query
	 */
	function getUrl(iSkip, iTop, sNextUrl) {
		var mDataUrl, mNextUrl;

		mDataUrl = URI.parse(dataUrl);

		/*
		 * Use $skiptoken from response to query the next items.
		 * OData V4 returns a relative path, while OData V2 returns
		 * an absolute path. Therefore we need to use the original
		 * URL to keep possible proxy settings and avoid any issues
		 * between OData V4 and V2
		 */
		if (sNextUrl) {
			mNextUrl = URI.parse(sNextUrl);
			mDataUrl.query = mNextUrl.query;
		} else { // Use $skip and $top
			mDataUrl.query = (mDataUrl.query || '')
				.replace(/\$skip\=[0-9]+/g, '$skip=' + iSkip)
				.replace(/\$top\=[0-9]+/g, '$top=' + iTop);
		}

		return (URI.serialize || URI.build)(mDataUrl);
	}

	/**
	 * Inner function that processes request handler exceptions.
	 *
	 * @param {string} sMessage - Error message.
	 */
	function fnOnError(sMessage) {
		fnProcessCallback({
			error: sMessage
		});
	}

	/**
	 * Inner function that processes the received data. Processing
	 * the data before executing the callback function allows to
	 * apply transformations to the data.
	 *
	 * @param {Object} oResult - The result object that is provided by the Promise resolve.
	 */
	function fnOnDataReceived(oResult) {
		var aData, sNextUrl, iFetchedRows, fPercent, iRemainingRows;
		var mCallbackParams = {};

		if (bCancelled) {
			return; // Cancelled by the application
		}

		/* Check for OData V4 result, if not present check for OData V2 result or apply default */
		aData = (oResult && oResult.value || (oResult.d && (oResult.d.results || oResult.d))) || oResult;
		aData = (Array.isArray(aData)) ? aData : [];
		iFetchedRows = aData.length;

		iAvailableRows += iFetchedRows;
		iRemainingRows = iTotalRows - iAvailableRows;
		fPercent = iAvailableRows / iTotalRows;

		mCallbackParams.finished = iFetchedRows === 0 || iRemainingRows <= 0; // Done criteria
		mCallbackParams.progress = Math.round(fPercent * 100);

		// Check if next url is provided
		sNextUrl = (oResult && oResult['@odata.nextLink'] || (oResult.d && oResult.d.__next)) || null;

		if (!mCallbackParams.finished) {
			// Trigger next page request before processing received data. Fetch only configured/max limit rows
			mRequest.dataUrl = getUrl(iAvailableRows, Math.min(iBatchSize, iRemainingRows), sNextUrl);
			RequestHandler
				.sendRequest(mRequest)
				.then(fnOnDataReceived)
				.catch(fnOnError);
		}

		mCallbackParams.rows = fnConvertData(aData); // Normalize data
		fnProcessCallback(mCallbackParams); // Return result
	}

	// Execution
	mRequest = {
		serviceUrl: cleanUrl(mDataSource.serviceUrl),
		dataUrl: getUrl(0, iBatchSize),
		method: mDataSource.useBatch ? 'BATCH' : 'GET',
		headers: mDataSource.headers
	};

	RequestHandler
		.sendRequest(mRequest)
		.then(fnOnDataReceived)
		.catch(fnOnError);

	return { cancel: function() { bCancelled = true; } };
}

module.exports = {
	requestData: requestData,
	getConverter: getDataConverter
};


//////////////////
// WEBPACK FOOTER
// ./client/src/provider/ODataDataProvider.js
// module id = 37
// module chunks = 1